import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BrushIcon from '../../../assets/svg/brush.svg?react';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

import CircularProgress from '@mui/material/CircularProgress';
import { useQualificationsCardForm } from './useQualificationsCardForm';
import styles from './QualificationsCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Employee } from '../../models/employee.model';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

type QualificationsCardProps = {
  simplified?: boolean;
  employee: Partial<Employee>;
};

export default function QualificationsCard({ employee }: QualificationsCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useQualificationsCardForm(employee);

  return (
    <Grid component='div' container className='inputs-editable'>
      <Grid component='div' size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid
                  component='div'
                  container
                  style={{ alignItems: 'center', flexWrap: 'nowrap', padding: '8px' }}
                >
                  <Grid
                    component='div'
                    size={{ xs: 11 }}
                    container
                    style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                  >
                    <Grid
                      component='div'
                      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      <SchoolOutlinedIcon />
                    </Grid>
                    <Grid component='div'>
                      <Typography variant='h6'>Qualifications</Typography>{' '}
                      {/*TODO : Add translation */}
                    </Grid>
                  </Grid>
                  <Grid
                    component='div'
                    size={{ xs: 1 }}
                    container
                    style={{ justifyContent: 'flex-end' }}
                  >
                    {!isEdited ? (
                      <Tooltip title={t('buttons.edit')} placement='top'>
                        <IconButton
                          id='qualificationsCardEditIconBtn'
                          onClick={toggleEditing}
                          sx={{ flexShrink: 0 }}
                        >
                          <BrushIcon id='qualificationsCardEditIconBtnBrushIcon' />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                          id='qualificationsCardCancelBtn'
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          id='qualificationsCardSaveBtn'
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={!isEdited}
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Grid component='div' container spacing={2}>
                  <Grid component='div' size={{ xs: 12 }}>
                    <Grid component='div' container spacing={2}>
                      <Grid component='div' size={{ xs: 12, sm: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.degree ?? ''}
                          label={t('labels.degreeOrCertificate')}
                          onChange={(e) => {
                            handleChange('degree')(e.target.value);
                          }}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid component='div' size={{ xs: 12, sm: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.university_name ?? ''}
                          label={t('labels.institutionName')}
                          onChange={(e) => {
                            handleChange('university_name')(e.target.value);
                          }}
                          disabled={!isEdited}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid component='div' size={{ xs: 12 }}>
                    <Grid component='div' container spacing={2}>
                      <Grid component='div' size={{ xs: 12 }}>
                        <Grid component='div' container spacing={2}>
                          <Grid component='div' size={{ xs: 12, sm: 6 }}>
                            {' '}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label={t('labels.startDate')}
                                value={formData.start_date ? dayjs(formData.start_date) : null}
                                onChange={(newValue) => {
                                  const formattedDate = newValue
                                    ? newValue.format('YYYY-MM-DD')
                                    : null;
                                  handleChange('start_date')(formattedDate);
                                }}
                                disabled={!isEdited}
                                format='DD/MM/YYYY'
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid component='div' size={{ xs: 12, sm: 6 }}>
                            {' '}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label={t('labels.endDate')}
                                value={formData.end_date ? dayjs(formData.end_date) : null}
                                onChange={(newValue) => {
                                  // Format date as YYYY-MM-DD for the form data
                                  const formattedDate = newValue
                                    ? newValue.format('YYYY-MM-DD')
                                    : null;
                                  handleChange('end_date')(formattedDate);
                                }}
                                disabled={!isEdited}
                                format='DD/MM/YYYY'
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {
                  <Grid
                    component='div'
                    container
                    className={styles.container_margin_top}
                    style={{ alignItems: 'center' }}
                  >
                    <Grid component='div'>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
